import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { AddressMrhComponent } from "@shared/components/offers/MRH/address-mrh/address-mrh.component";
import { DetailInfoCardMrhComponent } from "@shared/components/offers/MRH/detail-info-card-mrh/detail-info-card-mrh.component";
import { ServiceOfferingsMrhComponent } from "@shared/components/offers/MRH/service-offerings-mrh/service-offerings-mrh.component";
import { DetailInfoCardPjComponent } from "@shared/components/offers/PJ/detail-info-card-pj/detail-info-card-pj.component";
import { ServiceOfferingsPjComponent } from "@shared/components/offers/PJ/service-offerings-pj/service-offerings-pj.component";
import { TitlePjComponent } from "@shared/components/offers/PJ/title-pj/title-pj.component";
import { DetailInfoCardSaComponent } from "@shared/components/offers/SA/detail-info-card-sa/detail-info-card-sa.component";
import { ServiceOfferingsSaComponent } from "@shared/components/offers/SA/service-offerings-sa/service-offerings-sa.component";
import { TitleSaComponent } from "@shared/components/offers/SA/title-sa/title-sa.component";
import { PaymentInformationsComponent } from "@shared/components/payment-informations/payment-information.component";
import { StatusCardComponent } from "@shared/components/status-card/status-card.component";
import { ID, ID_PARAM } from "@shared/constants/route-variables.constants";
import { CONTRACTDETAILS } from "@shared/constants/route.constants";
import { Branches } from "@shared/enums/branches";
import { DocumentType } from "@shared/enums/documents.type";
import { PackagesType } from "@shared/enums/packages-type";
import { PolicyStates } from "@shared/enums/policy-states";
import { ReceiptStates } from "@shared/enums/receipt-states.enum";
import { Address } from "@shared/models/address";
import { GdaTypes } from "@shared/models/contract-options-state.model";
import { DocumentsVm, PolicyDetail } from "@shared/models/policy-details";
import { PolicyReceiptsYearVm } from "@shared/models/policy-receipts.model";
import { StatusConfig } from "@shared/models/status-config.model";
import { OffersTypeService } from "@shared/services/offers-type.service";
import { PolicyStatusService } from "@shared/services/policy-status.service";
import { PolicyService } from "@shared/services/policy.service";
import { Guid } from "guid-typescript";
import { ButtonModule } from "primeng/button";
import { ReceiptsDetailInfoComponent } from "./receipts-detail-info/receipts-detail-info.component";
import { MetadataValueType } from "@shared/enums/metadata-value-type.enum";
import { DocumentService } from "@shared/services/document.service";
import { DocumentTypes } from "@shared/enums/document-type.enum";
import { MessageServiceComponent } from "@shared/components/message-service/message-service.component";
import { ERROR_SEVERITY, ERROR_SUMMARY_MESSAGE_KEY } from "@shared/constants/message-service.constants";
import { GenerateEcheancierDialogComponent } from "./generate-echeancier-dialog/generate-echeancier-dialog.component";

@Component({
  selector: "app-receipts-detail",
  standalone: true,
  imports: [
    CommonModule,
    ReceiptsDetailInfoComponent,
    PaymentInformationsComponent,
    ServiceOfferingsMrhComponent,
    ServiceOfferingsPjComponent,
    ServiceOfferingsSaComponent,
    TitlePjComponent,
    TitleSaComponent,
    AddressMrhComponent,
    StatusCardComponent,
    DetailInfoCardMrhComponent,
    DetailInfoCardPjComponent,
    DetailInfoCardSaComponent,
    ButtonModule,
    TranslateModule,
    MessageServiceComponent,
    GenerateEcheancierDialogComponent,
  ],
  templateUrl: "./receipts-detail.component.html",
})
export class ReceiptsDetailComponent implements OnInit {
  paymentSchedule: PolicyReceiptsYearVm[] = [];
  PolicyStates = PolicyStates;
  needPayment = false;
  policyId!: Guid;
  policyDetails!: PolicyDetail;
  reference!: string;
  isMenuOpen = false;
  policyStatusConfigStatus!: Record<string, StatusConfig>;
  isGdaAllowed = false;
  addressData!: Address;
  isMrhBranch = false;
  isPJBranch = false;
  isSaBranch = false;
  isPolicyWaitingForSignature = false;
  isPolicyActive = false;
  isConfortProduct!: boolean;
  fSDocument!: DocumentsVm;
  messageId!: Guid;
  severityMessage!: string;
  summaryMessage!: string;
  detailMessage!: string;

  generateEcheancierDialogVisible = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly policyService: PolicyService,
    private readonly policyStatusService: PolicyStatusService,
    private readonly offerTypeService: OffersTypeService,
    private readonly documentService: DocumentService,
    private readonly translateService: TranslateService
  ) {}

  ngOnInit() {
    this.policyStatusConfigStatus = this.policyStatusService.getStatusConfig();
    this.policyId = this.route.snapshot.params[ID];
    this.loadPolicyDetails();
    this.loadPaymentSchedule();
  }

  loadPolicyDetails() {
    this.policyService.getPolicyDetails(this.policyId).subscribe({
      next: policyDetails => {
        this.policyDetails = policyDetails;
        this.reference = this.policyDetails.reference;
        this.fillAddressData();
        this.fSDocument =
          policyDetails.documents.find(doc => doc.documentTypeCode === DocumentType.FS) || ({} as DocumentsVm);
        this.isPolicyWaitingForSignature = this.policyStatusService.isPolicyWaitingForSignature(this.policyDetails);
        this.isPolicyActive = this.policyStatusService.isPolicyActive(this.policyDetails);
        this.isMrhBranch = this.offerTypeService.isPolicyDetailsBranch(this.policyDetails, Branches.MRH);
        if (!this.isMrhBranch) {
          this.isPJBranch = this.offerTypeService.isPolicyDetailsBranch(this.policyDetails, Branches.PJ);
          if (!this.isPJBranch)
            this.isSaBranch = this.offerTypeService.isPolicyDetailsBranch(this.policyDetails, Branches.SA);
        }
        this.isGdaAllowed =
          this.policyDetails.gda === GdaTypes.False_Positive ||
          this.policyDetails.gda === GdaTypes.Confirmed_Authorized ||
          this.policyDetails.gda === null;

        const formulaCode = this.policyDetails.formulasAndOptionsInformations.formulaCode;
        this.isConfortProduct = formulaCode === PackagesType.Confort || formulaCode === PackagesType.ConfortPlus;
      },
    });
  }

  loadPaymentSchedule() {
    this.policyService.getMemberReceiptsByPolicyId(this.policyId).subscribe(data => {
      this.paymentSchedule = data;
      this.needPayment = this.paymentSchedule
        .flatMap(x => x.receipts)
        .some(receipt => receipt.statusCode === ReceiptStates.IMP);
    });
  }

  resetView() {
    const contractDetailsPath = CONTRACTDETAILS.replace(ID_PARAM, this.policyId.toString());
    this.router.navigate([contractDetailsPath]);
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
  }

  fillAddressData() {
    if (this.policyDetails) {
      this.addressData = {
        address: this.policyDetails.address,
        postalCode: this.policyDetails.postalCode,
        city: this.policyDetails.city,
      };
    }
  }

  generateEcheancier(withStatus: boolean) {
    this.generateEcheancierDialogVisibleChange(false);

    const metadata: Partial<Record<MetadataValueType, string>> | null | undefined = {
      [MetadataValueType.EcheancierWithStatus]: withStatus.toString(),
    };
    this.documentService.generateCertificate(metadata, DocumentTypes.ECHEANCIER, this.policyId).subscribe({
      next: result => {
        if (result) {
          this.documentService.onDownload(result.certificateId);
        } else {
          this.echeancierCreationFailed();
        }
      },
      error: () => {
        this.echeancierCreationFailed();
      },
    });
  }

  generateEcheancierDialogVisibleChange(visible: boolean) {
    this.generateEcheancierDialogVisible = visible;
  }

  generationEcheancierIsAvailable() {
    return this.policyDetails?.status !== PolicyStates.Wait4Sign && this.paymentSchedule?.length > 0;
  }

  private echeancierCreationFailed() {
    this.messageId = Guid.create();
    this.severityMessage = ERROR_SEVERITY;
    this.summaryMessage = this.translateService.instant(ERROR_SUMMARY_MESSAGE_KEY);
    this.detailMessage = this.translateService.instant("receipt.echeancierCreationFailed");
  }
}
